/* eslint-disable max-lines */
import { Frame } from '@hakimo-ui/hakimo/types';
import {
  FramePlayer,
  SpecialZones,
  SpecialZonesOverlay,
} from '@hakimo-ui/hakimo/ui-elements';
import { PlayIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FrameBuffer,
  frameIndexAt,
  sortFrames,
} from '../../frame-sequence-player';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
  useAlarmGroupTime,
} from '../alarm-provider/AlarmGroupProvider';

// A frame the buffer still hasn't decoded after this long is treated as unavailable
// (genuinely broken/expired) so the clock skips past it instead of stalling forever.
// Comfortably longer than any real decode, so a slow-but-valid 3G frame is never
// skipped — the seeker just holds until it lands.
const SKIP_STUCK_MS = 10_000;

interface Props {
  /** This camera's full frame sequence (all its alarms, merged + sorted). */
  frames: Frame[];
  specialZones?: SpecialZones;
}

export function FramesMedia(props: Props) {
  const { frames: rawFrames, specialZones } = props;
  const cursorRef = useRef(0);
  // Tracks how long the playhead has been stalled on the current (undecoded) frame.
  const stallRef = useRef<{ target: number; since: number } | null>(null);
  // The element FramePlayer paints into (its canvas), reported via callback.
  // Held in state (not a ref) and wrapped in a fresh ref object per identity,
  // so SpecialZonesOverlay re-anchors when the canvas is replaced.
  const [mediaEl, setMediaEl] = useState<
    HTMLCanvasElement | HTMLImageElement | null
  >(null);
  const mediaRef = useMemo(() => ({ current: mediaEl }), [mediaEl]);
  // The frame on screen: index + the decoded image FramePlayer paints. Keeping
  // them in one state means the canvas can only ever be asked to paint a frame
  // that is actually decoded — the pixels can't lag the index.
  const [shown, setShown] = useState<{
    index: number;
    img: HTMLImageElement | null;
  }>({ index: 0, img: null });
  const [loading, setLoading] = useState(true);

  const { location, clock } = useAlarmGroupStatic();
  const { isSeekerPaused, toggleSeekerPaused } = useAlarmGroupTime();
  const { reportFramesReady } = useAlarmGroupPlayback();
  // Stable per-instance id so the provider can track this camera's readiness.
  const trackId = useId();

  const frames = useMemo(() => sortFrames(rawFrames), [rawFrames]);

  // Rebuild the decode buffer only when the frame SET changes (count / first / last
  // ts), not on every new array identity from a camera-details refetch — a rebuild
  // resets readiness and briefly stalls the shared clock for all cameras. Read the
  // frames through a ref so an identity-only change doesn't re-run the effect.
  const framesKey =
    frames.length === 0
      ? '0'
      : `${frames.length}:${frames[0].timestamp}:${
          frames[frames.length - 1].timestamp
        }`;
  const framesRef = useRef(frames);
  framesRef.current = frames;

  // Frame selection driven by the continuous clock (rAF), not a fixed tick: the
  // target frame is the most recent frame at/before clock time (frameIndexAt) —
  // this kills the rate-aliasing repeat/skip and keeps cameras in lockstep. We only
  // ADVANCE the shown frame to one that is actually decoded; until then we hold the
  // last good frame and show a spinner, then skip forward once it lands.
  //
  // The decode-ahead buffer is created INSIDE this effect (not a useMemo) so its
  // lifecycle is tied to the effect: a dev StrictMode remount gets a fresh buffer.
  // A useMemo'd buffer survives the remount but gets disposed by the cleanup,
  // leaving a dead buffer whose update() early-returns and never decodes.
  useEffect(() => {
    const activeFrames = framesRef.current;
    if (!clock || activeFrames.length === 0) {
      setLoading(false); // no footage to decode — don't spin forever
      return;
    }
    const buffer = new FrameBuffer(activeFrames);
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const target = frameIndexAt(
        activeFrames,
        clock.getTime(),
        cursorRef.current
      );
      cursorRef.current = target;
      buffer.update(target);
      const decoded = buffer.get(target);
      let ready = !!decoded;
      if (decoded) {
        setShown((prev) =>
          prev.index === target && prev.img === decoded
            ? prev
            : { index: target, img: decoded }
        );
        stallRef.current = null;
      } else {
        // Not decoded: hold the seeker (report not-ready) — but if we've been stuck
        // on THIS frame far longer than any real decode should take, treat it as
        // unavailable and skip so the clock (and the other cameras) can advance past
        // a genuinely broken/expired frame.
        const nowMs = performance.now();
        const stall = stallRef.current;
        if (!stall || stall.target !== target) {
          stallRef.current = { target, since: nowMs };
        } else if (nowMs - stall.since > SKIP_STUCK_MS) {
          ready = true;
        }
      }
      // Report readiness every frame (deduped provider-side): the clock stalls
      // while this camera lacks its current frame, so the seeker holds instead of
      // running ahead of the footage under a slow network.
      reportFramesReady(trackId, ready);
      setLoading((prev) => (prev === !ready ? prev : !ready));
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      buffer.dispose();
      // Leaving the grid: drop this camera's stall so it can't gate the others.
      reportFramesReady(trackId, true);
    };
    // framesKey (not frames) so an identity-only change doesn't rebuild the buffer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clock, framesKey, reportFramesReady, trackId]);

  const onClickFrame = () => {
    toggleSeekerPaused();
  };

  // No frames to show (e.g. the edge failed to upload them to S3): render a
  // placeholder instead of driving FramePlayer with an empty list, which
  // otherwise bubbles an error to the page-level boundary.
  if (!Array.isArray(frames) || frames.length === 0) {
    return (
      <div className="text-ondark-text-1 flex h-full w-full items-center justify-center text-sm">
        No frames available
      </div>
    );
  }

  return (
    <div className="relative h-full w-full" onClick={onClickFrame}>
      <div className="h-full w-full">
        <FramePlayer
          frames={frames}
          currentFrameIndex={shown.index}
          decodedFrame={shown.img}
          elaborateFrames
          timeZone={location?.timezone ?? 'UTC'}
          // Unconditional: this component only renders when the camera has
          // frames, and frames get faces regardless of product. Matched faces
          // exist only on Vision-sourced frames, and FramePlayer renders
          // nothing when a frame carries none.
          showMatchedFaces={true}
          onMediaElement={setMediaEl}
        />
      </div>
      <SpecialZonesOverlay mediaRef={mediaRef} specialZones={specialZones} />
      {loading && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-white/25 border-t-white" />
        </div>
      )}
      {isSeekerPaused && (
        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
          <div className="transform transition-all duration-300 ease-in-out">
            <PlayIcon className="text-ondark-text-1 h-6 w-6 drop-shadow-lg" />
          </div>
        </div>
      )}
    </div>
  );
}

export default FramesMedia;
